import React from 'react'
import { motion } from 'framer-motion'
import { useRouter } from 'next/router'
import { cva } from 'class-variance-authority'

// Shadcn UI 组件
import { MessageSquare, GraduationCap, Heart, Brain, Store, Zap, ArrowRight, LineChart, Briefcase, PenTool, Lightbulb, Users, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

const featureCardVariants = cva('relative flex h-full overflow-hidden transition-all duration-300 group', {
  variants: {
    variant: {
      default: 'bg-background/50 hover:bg-accent/50 hover:shadow-md',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const scenarioCardVariants = cva('relative flex h-full overflow-hidden transition-all duration-300 group', {
  variants: {
    variant: {
      business:
        'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200/50 hover:border-blue-300/80 dark:from-blue-950/20 dark:to-indigo-950/20 dark:border-blue-800/30 dark:hover:border-blue-700/60',
      creative:
        'bg-gradient-to-br from-amber-50 to-rose-50 border-amber-200/50 hover:border-amber-300/80 dark:from-amber-950/20 dark:to-rose-950/20 dark:border-amber-800/30 dark:hover:border-amber-700/60',
      education:
        'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200/50 hover:border-emerald-300/80 dark:from-emerald-950/20 dark:to-teal-950/20 dark:border-emerald-800/30 dark:hover:border-emerald-700/60',
      personal:
        'bg-gradient-to-br from-purple-50 to-fuchsia-50 border-purple-200/50 hover:border-purple-300/80 dark:from-purple-950/20 dark:to-fuchsia-950/20 dark:border-purple-800/30 dark:hover:border-purple-700/60',
    },
  },
  defaultVariants: {
    variant: 'business',
  },
})

const ChatIntro = () => {
  const router = useRouter()

  const features = [
    {
      title: '专业写作',
      description: '从学术论文到创意写作，AI 助手可以帮助您撰写各类高质量内容，提供思路拓展与精确修改建议。',
      icon: <MessageSquare className="h-6 w-6 text-blue-500" />,
      badge: '热门',
    },
    {
      title: '学习辅助',
      description: '覆盖各类学科知识，提供清晰解释和个性化学习路径，让知识获取变得轻松高效。',
      icon: <GraduationCap className="h-6 w-6 text-emerald-500" />,
    },
    {
      title: '智能交流',
      description: '拥有流畅自然的对话能力，既可以是您的思想伙伴，也可以是情绪支持者，随时提供贴心陪伴。',
      icon: <Heart className="h-6 w-6 text-rose-500" />,
    },
    {
      title: '先进语言模型',
      description: '基于最新人工智能技术，理解能力和表达能力媲美人类，能处理复杂指令和多轮对话。',
      icon: <Brain className="h-6 w-6 text-purple-500" />,
    },
    {
      title: 'AI 助手市场',
      description: '丰富的预设助手库，覆盖工作、学习、生活各方面需求，也可自定义专属助手。',
      icon: <Store className="h-6 w-6 text-amber-500" />,
    },
    {
      title: '高效集成',
      description: '无缝集成到您的工作流程中，支持多种平台，随时随地提供智能支持。',
      icon: <Zap className="h-6 w-6 text-cyan-500" />,
    },
  ]

  const scenarios = [
    {
      title: '商务专业助手',
      description: '智能处理商务文档、撰写专业邮件、总结会议内容，助您高效完成工作任务。',
      features: ['撰写商务邮件和报告', '智能分析市场数据', '会议纪要自动生成'],
      icon: <Briefcase className="h-8 w-8" />,
      variant: 'business',
      stat: '提升工作效率高达 300%',
      statIcon: <Clock className="h-4 w-4" />,
    },
    {
      title: '创意创作伙伴',
      description: '激发灵感、优化文案、提供创意反馈，让您的创作之路更加顺畅。',
      features: ['故事构思与创意发想', '文学作品润色优化', '营销文案精准打磨'],
      icon: <PenTool className="h-8 w-8" />,
      variant: 'creative',
      stat: '创意产出质量提升 250%',
      statIcon: <Lightbulb className="h-4 w-4" />,
    },
    {
      title: '学习成长导师',
      description: '个性化学习辅导、知识点解析、考试备考指导，让学习更高效。',
      features: ['概念分解与简明讲解', '定制学习路径规划', '智能题目解析'],
      icon: <GraduationCap className="h-8 w-8" />,
      variant: 'education',
      stat: '学习理解速度提升 200%',
      statIcon: <Brain className="h-4 w-4" />,
    },
    {
      title: '数字生活顾问',
      description: '从日常规划到个人发展，提供贴心建议与决策支持，让生活更有序。',
      features: ['个人目标管理', '情感分析与建议', '生活习惯优化'],
      icon: <Users className="h-8 w-8" />,
      variant: 'personal',
      stat: '满意度评分高达 4.9/5',
      statIcon: <Heart className="h-4 w-4" />,
    },
  ]

  // 动画配置
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }

  return (
    <section className="relative overflow-hidden bg-background py-24">
      {/* 背景装饰元素 */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-[10%] top-[20%] h-[45rem] w-[45rem] rounded-full bg-primary/5" />
        <div className="absolute -right-[10%] top-[60%] h-[40rem] w-[40rem] rounded-full bg-blue-500/5" />
        <div className="absolute left-1/2 top-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-purple-500/5" />
      </div>

      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-4xl text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 bg-gradient-to-r from-foreground via-foreground/90 to-foreground bg-clip-text text-4xl font-bold tracking-tight text-transparent sm:text-5xl"
          >
            重新定义智能对话体验
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10 text-xl leading-relaxed text-muted-foreground"
          >
            <span className="bg-gradient-to-r from-blue-600 to-primary bg-clip-text text-transparent">能交谈、能创作，帮您轻松处理复杂事务</span>
          </motion.p>
        </div>

        <Tabs defaultValue="features" className="mx-auto mt-16 max-w-6xl">
          <div className="flex justify-center">
            <TabsList className="grid w-[400px] grid-cols-2">
              <TabsTrigger value="features" className="rounded-lg text-sm">
                功能特性
              </TabsTrigger>
              <TabsTrigger value="scenarios" className="rounded-lg text-sm">
                应用场景
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="features" className="mt-8">
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => (
                <motion.div key={feature.title} variants={itemVariants} className="h-full">
                  <Card className={featureCardVariants({ variant: feature.variant })}>
                    <CardContent className="flex h-full flex-col p-6">
                      <div className="mb-4 flex items-center justify-between">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-background p-2 shadow-sm ring-1 ring-border/10">{feature.icon}</div>
                        {feature.badge && (
                          <Badge variant="secondary" className="bg-primary/10 text-primary">
                            {feature.badge}
                          </Badge>
                        )}
                      </div>
                      <h3 className="mb-3 text-xl font-semibold tracking-tight">{feature.title}</h3>
                      <p className="mb-4 flex-grow text-sm leading-relaxed text-muted-foreground">{feature.description}</p>
                      <div className="mt-auto pt-3">
                        <Button variant="ghost" size="sm" className="group/btn -mx-2 h-8 text-xs font-medium text-primary">
                          了解更多
                          <ArrowRight className="ml-1 h-3 w-3 transition-transform duration-300 group-hover/btn:translate-x-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>

          <TabsContent value="scenarios" className="mt-8">
            <div className="grid gap-6 md:grid-cols-2">
              {scenarios.map((scenario, index) => (
                <motion.div
                  key={scenario.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className={`${scenarioCardVariants({ variant: scenario.variant })} h-full border shadow-sm hover:shadow-md`}>
                    <CardContent className="flex h-full flex-col p-6">
                      <div className="mb-5 flex items-center space-x-4">
                        <div
                          className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br shadow-sm
                          ${
                            scenario.variant === 'business'
                              ? 'from-blue-500 to-indigo-600 text-white'
                              : scenario.variant === 'creative'
                              ? 'from-amber-500 to-rose-600 text-white'
                              : scenario.variant === 'education'
                              ? 'from-emerald-500 to-teal-600 text-white'
                              : 'from-purple-500 to-fuchsia-600 text-white'
                          }`}
                        >
                          {scenario.icon}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold tracking-tight">{scenario.title}</h3>
                          <div className="flex items-center space-x-1 text-sm font-medium text-muted-foreground">
                            {scenario.statIcon}
                            <span>{scenario.stat}</span>
                          </div>
                        </div>
                      </div>

                      <p className="mb-5 text-sm leading-relaxed text-muted-foreground">{scenario.description}</p>

                      <ul className="mb-4 space-y-2">
                        {scenario.features.map((feature, i) => (
                          <li key={+i} className="flex items-center text-sm">
                            <span
                              className={`mr-2 flex h-5 w-5 items-center justify-center rounded-full 
                              ${
                                scenario.variant === 'business'
                                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                  : scenario.variant === 'creative'
                                  ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                                  : scenario.variant === 'education'
                                  ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                                  : 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                              }`}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <div className="mt-auto pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className={`w-full justify-center 
                            ${
                              scenario.variant === 'business'
                                ? 'border-blue-200 hover:bg-blue-50 hover:text-blue-700 dark:border-blue-800 dark:hover:bg-blue-900/30 dark:hover:text-blue-400'
                                : scenario.variant === 'creative'
                                ? 'border-amber-200 hover:bg-amber-50 hover:text-amber-700 dark:border-amber-800 dark:hover:bg-amber-900/30 dark:hover:text-amber-400'
                                : scenario.variant === 'education'
                                ? 'border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 dark:border-emerald-800 dark:hover:bg-emerald-900/30 dark:hover:text-emerald-400'
                                : 'border-purple-200 hover:bg-purple-50 hover:text-purple-700 dark:border-purple-800 dark:hover:bg-purple-900/30 dark:hover:text-purple-400'
                            }`}
                        >
                          开始使用
                          <ArrowRight className="ml-2 h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-24 flex flex-col items-center rounded-2xl bg-gradient-to-b from-background via-accent/10 to-accent/20 p-12 shadow-sm"
        >
          <div className="mb-16 text-center">
            <p className="mb-12 text-xl font-medium text-muted-foreground">
              加入全球超过 <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-2xl font-bold text-transparent">2,000,000+</span> 用户的智能体验
            </p>
            <div className="flex flex-wrap justify-center gap-16 lg:gap-24">
              {[
                { label: '对话精准度', value: '98%', icon: <LineChart className="h-6 w-6 text-primary/80" /> },
                { label: '平均响应时间', value: '<1s', icon: <Clock className="h-6 w-6 text-primary/80" /> },
                { label: '用户满意度', value: '4.9/5', icon: <Heart className="h-6 w-6 text-primary/80" /> },
              ].map((stat) => (
                <motion.div
                  key={stat.label}
                  whileHover={{ y: -5 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  className="flex min-w-[180px] flex-col items-center rounded-xl bg-card p-6 shadow-sm ring-1 ring-border/40"
                >
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 p-2">{stat.icon}</div>
                  <div className="relative mb-2 text-5xl font-bold text-primary">
                    {stat.value}
                    <span className="absolute -right-1 -top-1 flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                    </span>
                  </div>
                  <div className="text-base font-medium text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="shadow-lg">
              <Button
                onClick={() => router.push(`/chat`)}
                size="lg"
                className="group bg-gradient-to-r from-primary to-primary/90 px-10 py-6 text-lg font-medium shadow-md hover:shadow-lg"
              >
                立即体验
                <ArrowRight className="ml-3 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </motion.div>
            <Button variant="outline" size="lg" className="border-primary/20 bg-background/80 px-10 py-6 text-lg font-medium text-foreground hover:bg-primary/5">
              了解详情
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ChatIntro
